/**
 * Known-Databases Tracker (Slice 5 of Connection Diagnostics).
 *
 * When a per-database operation succeeds against a connection (query
 * execution, table-explorer fetch, etc.), append that database name to
 * the connection's `knownDatabases` list. Used as the smarter-fallback
 * tree when `getDatabases()` listing fails — essential for contained-user
 * shapes on Azure SQL where SQL itself cannot enumerate the user's DBs.
 *
 * - Capped at 50 entries (LRU): on overflow, the oldest entry is dropped.
 * - De-duplicated case-insensitively for MSSQL/MySQL, case-sensitively
 *   for PostgreSQL/Oracle/SQLite — match each engine's identifier case.
 * - Throttled per (connectionId, database) per-session: an in-memory set
 *   prevents redundant persistence calls for the same DB in the same run.
 * - COALESCED per connection: the post-connect object preload sees a burst of
 *   newly-seen databases within a second or two, and each one used to be its
 *   own `updateConnection` — i.e. its own encrypt-and-rewrite of the whole
 *   connections file on the main thread, right in the window where the user is
 *   waiting for the tree. The burst is now buffered and written once.
 */

import type { Connection } from '../../../types/electron';
import { useConnectionStore } from '../store';
import { caseFoldFor } from '../utils/databaseNameCase';

const KNOWN_DATABASES_CAP = 50;

/** Per-process set of `(connectionId|database)` keys we already persisted in this session. */
const persistedThisSession = new Set<string>();

/**
 * How long a connection's newly-seen databases are buffered before the single
 * write. Long enough to swallow a preload burst, short enough that the record
 * is on disk well before anything a user does next.
 */
const KNOWN_DATABASES_FLUSH_DELAY_MS = 1_000;

/** Databases seen but not yet written, per connection, in the order seen. */
const pendingByConnection = new Map<string, string[]>();
const flushTimerByConnection = new Map<string, ReturnType<typeof setTimeout>>();

function appendDedupLru(
  existing: string[] | undefined,
  next: string,
  fold: (s: string) => string,
): string[] {
  const folded = fold(next);
  const filtered = (existing ?? []).filter((db) => fold(db) !== folded);
  filtered.push(next);
  while (filtered.length > KNOWN_DATABASES_CAP) {
    filtered.shift();
  }
  return filtered;
}

/**
 * Write the databases buffered for one connection — ONE `updateConnection` for
 * the whole burst.
 *
 * The buffer is dropped even when the write cannot happen (the connection was
 * deleted meanwhile): `persistedThisSession` already holds those keys, so the
 * databases are not re-offered this session, and a deleted connection has no
 * record to append them to.
 *
 * Nothing flushes this on quit. That is deliberate: the only cost of losing
 * the last buffered window is that a `knownDatabases` fallback entry has to be
 * re-learned on the next run, which happens on the first successful query
 * against that database — cheaper than making shutdown wait on a file write.
 */
function flushKnownDatabases(connectionId: string): void {
  flushTimerByConnection.delete(connectionId);
  const pending = pendingByConnection.get(connectionId);
  pendingByConnection.delete(connectionId);
  if (!pending || pending.length === 0) return;

  const store = useConnectionStore.getState();
  const connection = store.connections.find((c) => c.id === connectionId);
  if (!connection) return;

  const fold = caseFoldFor(connection.engine);
  // Folded through the same append the un-coalesced path used, once per
  // database and in the order they were seen, so the LRU tail still reads
  // "most recently touched last".
  let nextKnown = connection.knownDatabases;
  for (const database of pending) {
    nextKnown = appendDedupLru(nextKnown, database, fold);
  }
  const updated: Connection = { ...connection, knownDatabases: nextKnown };
  void store.updateConnection(updated);
}

/**
 * Mark `database` as known-good for `connectionId`, and persist the connection
 * record — once per burst — via the connection store's `updateConnection`.
 * Cheap to call on every successful query: repeated invocations for the same
 * (connectionId, database) in one session are dropped by an in-memory set, and
 * a burst of DIFFERENT databases is buffered into a single write.
 */
export function recordKnownDatabase(connectionId: string, database: string): void {
  if (!database || !connectionId) return;
  const key = `${connectionId}|${database}`;
  if (persistedThisSession.has(key)) return;

  const store = useConnectionStore.getState();
  const connection = store.connections.find((c) => c.id === connectionId);
  if (!connection) return;

  const fold = caseFoldFor(connection.engine);
  // The buffered databases are NOT on the store record yet, so they have to be
  // consulted here too — otherwise a burst would queue the same database twice
  // and the second copy would evict a real entry from the 50-item cap.
  const pending = pendingByConnection.get(connectionId) ?? [];
  const alreadyKnown = [...(connection.knownDatabases ?? []), ...pending].some(
    (db) => fold(db) === fold(database),
  );
  persistedThisSession.add(key);
  if (alreadyKnown) return;

  pendingByConnection.set(connectionId, [...pending, database]);
  if (!flushTimerByConnection.has(connectionId)) {
    flushTimerByConnection.set(
      connectionId,
      setTimeout(() => flushKnownDatabases(connectionId), KNOWN_DATABASES_FLUSH_DELAY_MS),
    );
  }
}

/** Test-only helper to clear the in-process dedup cache and pending writes. */
export function __resetKnownDatabasesTrackerForTests(): void {
  persistedThisSession.clear();
  for (const timer of flushTimerByConnection.values()) clearTimeout(timer);
  flushTimerByConnection.clear();
  pendingByConnection.clear();
}

/**
 * Pure helper used by tests to verify dedup/case-fold/LRU behaviour
 * without going through the connection store.
 */
export function appendKnownDatabaseForTesting(
  existing: string[] | undefined,
  next: string,
  engine?: string,
): string[] {
  return appendDedupLru(existing, next, caseFoldFor(engine));
}
